import { fetchWithAuth, API_BASE } from "@/lib/auth";
import type { ActivityMeetingItem } from "@/lib/dashboard/phase";
import type { MyGroupItem } from "@/components/dashboard/sections/MyGroupsSection";
import type { ReviewQueueItem } from "@/components/dashboard/sections/OngoingActivitiesSection";

const BASE = `${API_BASE}/api/v1/dashboard`;

export interface RawNotice {
    id: string;
    title?: string;
    message?: string;
    tag?: string;
    created_at?: string;
    [key: string]: unknown;
}

export type ActionSeverity = "blocking" | "due_soon" | "open";

export type ActionKind =
    | "admissions_vote"
    | "board_ratify"
    | "meeting_review"
    | "governance_task"
    | "policy_question"
    | "gig_commitment"
    | "event_helper";

/** One row of the "Needs you" rail. Source-agnostic by design — see
 *  `ActionItem` in `backend/app/api/routes/dashboard.py`. */
export interface ActionItem {
    id: string;
    kind: ActionKind;
    severity: ActionSeverity;
    title: string;
    context: string;
    entity_type: string | null;
    entity_id: number | null;
    due_at: string | null;
    href: string;
}

export interface AdmissionsCounts {
    is_committee_member: boolean;
    is_board_member: boolean;
    awaiting_count: number;
    board_awaiting_count: number;
    admissions_committee_id: number | null;
    board_committee_id: number | null;
}

export interface HomeSnapshot {
    groups: MyGroupItem[];
    notices: RawNotice[];
    review_queue: { pending: ReviewQueueItem[]; recently_reviewed: ReviewQueueItem[] };
    meetings: ActivityMeetingItem[];
    /** Future-only, ascending. The old Home sorted `meetings` descending. */
    upcoming: ActivityMeetingItem[];
    actions: ActionItem[];
    /** Folded in so Home and the sidebar stop each calling `/admissions-committee/access`. */
    admissions: AdmissionsCounts;
}

const EMPTY_ADMISSIONS: AdmissionsCounts = {
    is_committee_member: false,
    is_board_member: false,
    awaiting_count: 0,
    board_awaiting_count: 0,
    admissions_committee_id: null,
    board_committee_id: null,
};

const EMPTY: HomeSnapshot = {
    groups: [],
    notices: [],
    review_queue: { pending: [], recently_reviewed: [] },
    meetings: [],
    upcoming: [],
    actions: [],
    admissions: EMPTY_ADMISSIONS,
};

// Home and Activities render the same snapshot. Sharing one in-flight promise
// (and holding the result briefly) means tabbing between them costs nothing.
const TTL_MS = 30_000;
let cached: { at: number; promise: Promise<HomeSnapshot> } | null = null;

export function getHomeSnapshot(options: { force?: boolean } = {}): Promise<HomeSnapshot> {
    if (!options.force && cached && Date.now() - cached.at < TTL_MS) {
        return cached.promise;
    }
    const promise = fetchWithAuth(`${BASE}/home`).then(async (res) => {
        if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            throw new Error(data.detail || `Failed to load dashboard (${res.status})`);
        }
        const data: Partial<HomeSnapshot> = await res.json();
        return { ...EMPTY, ...data };
    });
    // A failed load must not be served to the next caller from cache.
    promise.catch(() => { if (cached?.promise === promise) cached = null; });
    cached = { at: Date.now(), promise };
    return promise;
}

/** Call after anything that changes groups, meetings or the review queue. */
export function invalidateHomeSnapshot() {
    cached = null;
}
